import { useLayoutEffect, type RefObject } from "react";

/**
 * The custom property the dashboard layout takes off the window's height.
 * The demo and operator-view banners sit above the layout, and the layout is
 * exactly the height that is left, so the window itself never scrolls; without
 * the offset the page would be a banner taller than the window, and the
 * sidebar's usage meter would start off the bottom of the screen.
 */
export const BANNER_OFFSET_PROPERTY = "--app-banner-height";

/** Publishes the banner's rendered height as `--app-banner-height`. */
export function useBannerOffset(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const banner = ref.current;
    if (!banner) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(
        BANNER_OFFSET_PROPERTY,
        `${banner.offsetHeight}px`,
      );
    publish();
    // The banner wraps onto a second line on a narrow screen.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(publish);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_OFFSET_PROPERTY);
    };
  }, [ref]);
}
